import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Tooltip } from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { COLORS } from '../../../constants/radar';
import CVSectionsChecklist from './CVSectionsChecklist';

const CVCompletionGaugeCard = ({ cv, size = 50 }) => {
  // Generate the CV sections for the checklist
  const cvSections = useMemo(() => {
    // Check if cv and completion exist to prevent errors
    const completion = cv?.completion || {};
    
    return [
      {
        name: 'Personal Details',
        complete: Boolean(completion.hasPersonalDetails),
        items: null
      },
      {
        name: 'Professional Summary',
        complete: Boolean(completion.professionalSummaryLength > 100),
        items: null
      },
      {
        name: 'Skills',
        complete: Boolean(completion.generalSkillsCount >= 5),
        items: completion.generalSkillsCount
      },
      {
        name: 'Employment History',
        complete: Boolean(completion.employmentHistoryCount > 0),
        items: completion.employmentHistoryCount
      },
      {
        name: 'Education',
        complete: Boolean(completion.educationCount > 0),
        items: completion.educationCount
      },
      {
        name: 'Courses',
        complete: Boolean(completion.coursesCount > 0),
        items: completion.coursesCount
      },
      {
        name: 'Certifications',
        complete: Boolean(completion.certificationsCount > 0),
        items: completion.certificationsCount
      },
      {
        name: 'Languages',
        complete: Boolean(completion.languagesCount > 0),
        items: completion.languagesCount
      }
    ];
  }, [cv]);

  // Calculate completion percentage based on CV data
  const completionData = useMemo(() => {
    // Return default values if cv is undefined
    if (!cv) {
      return {
        percentage: 0,
        color: '#eb9393',
        completedSections: [],
        totalSections: 8,
        value: 0
      };
    }
    
    // Count completed sections
    const completedSections = cvSections.filter(section => section.complete);
    
    // Calculate percentage
    const completionPercentage = Math.round((completedSections.length / cvSections.length) * 100);
    
    // Determine the color based on percentage
    let color = COLORS.PRIMARY;
    if (completionPercentage < 30) {
      color = '#eb9393'; // Red-ish (same as Novice)
    } else if (completionPercentage < 50) {
      color = '#e58e56'; // Orange-ish (same as Beginner)
    } else if (completionPercentage < 70) {
      color = '#dea33a'; // Amber (same as Skillful)
    } else if (completionPercentage < 90) {
      color = '#5db276'; // Green (same as Experienced)
    } else {
      color = '#8a8ded'; // Purple (same as Expert)
    }
    
    return {
      percentage: completionPercentage,
      color,
      completedSections,
      totalSections: cvSections.length,
      value: completedSections.length / cvSections.length
    };
  }, [cv, cvSections]);
  
  // Calculate arc width based on size
  const arcWidth = Math.max(8, Math.round(size * 0.24)); // Scale arc width proportionally, with minimum of 8px
  const cornerRadius = Math.max(3, Math.round(size * 0.1)); // Scale corner radius proportionally, with minimum of 3px
  const fontSize = Math.max(0.5, size * 0.014); // Scale font size proportionally, with minimum of 0.5rem
  
  // If cv is undefined, show a placeholder or return null
  if (!cv) {
    return (
      <Box sx={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f1f5f9',
        borderRadius: '50%'
      }}>
        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
          N/A
        </Typography>
      </Box>
    );
  }
  
  return (
    <Tooltip 
      title={
        <Box sx={{ 
          bgcolor: 'white', 
          p: 1.5, 
          borderRadius: 1,
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          minWidth: '220px'
        }}>
          <Typography sx={{ 
            mb: 1, 
            fontWeight: 600,
            color: '#1e293b',
            fontSize: '0.875rem'
          }}>
            CV Completion Checklist
          </Typography>
          <CVSectionsChecklist sections={cvSections} />
        </Box>
      }
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'transparent',
            '& .MuiTooltip-arrow': {
              color: 'white'
            }
          }
        }
      }}
    >
      <Box sx={{ 
        width: size, 
        height: size, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        cursor: 'pointer'
      }}>
        <Gauge
          series={[
            {
              data: [{ value: completionData.value }],
              arcWidth: arcWidth,
              cornerRadius: cornerRadius,
            },
          ]}
          startAngle={-90}
          endAngle={90}
          value={Math.round(completionData.value*100)}
          track={{
            stroke: '#E0E0E0',
            strokeWidth: arcWidth,
            cornerRadius: cornerRadius
          }}
          sx={{
            width: '100%',
            height: '100%',
            [`& .${gaugeClasses.valueArc}`]: {
              fill: completionData.color,
            },
            [`& .${gaugeClasses.valueText}`]: {
              fontSize: `${fontSize}rem`,
              fontWeight: 'bold',
            },
          }}
          slots={{
            legend: () => null
          }}
        />
      </Box>
    </Tooltip>
  );
};

CVCompletionGaugeCard.propTypes = {
  cv: PropTypes.object.isRequired,
  size: PropTypes.number
};

export default React.memo(CVCompletionGaugeCard); 